
import { DocumentContext, ContextualSuggestion } from './contextualAnalysisService';
import { supabase } from '@/integrations/supabase/client';

export interface DocumentSession {
  conversationId: string;
  documents: DocumentContext[];
  lastAnalysis?: {
    summary: string;
    suggestions: ContextualSuggestion[];
    timestamp: Date;
  };
}

class DocumentContextManager {
  private sessions: Map<string, DocumentSession> = new Map();

  /**
   * Store document context for a conversation AND save to database
   */
  async setDocumentContext(
    conversationId: string, 
    context: DocumentContext,
    summary: string,
    suggestions: ContextualSuggestion[],
    userId: string
  ): Promise<void> {
    let session = this.sessions.get(conversationId);
    
    if (!session) {
      session = {
        conversationId,
        documents: [],
      };
      this.sessions.set(conversationId, session);
    }
    
    session.documents.push(context);
    session.lastAnalysis = {
      summary,
      suggestions,
      timestamp: new Date()
    };
    
    // Save to database for persistence
    await this.saveToDatabase(conversationId, summary, suggestions, context, userId);
    
    console.log(`📚 Document context stored for conversation ${conversationId} (memory + database)`);
  }

  /**
   * Save analysis to database - CORRIGIDO para funcionar sem conflitos
   */
  private async saveToDatabase(
    conversationId: string, 
    summary: string, 
    suggestions: ContextualSuggestion[], 
    context: DocumentContext,
    userId: string
  ): Promise<void> {
    try {
      console.log('💾 Salvando análise no banco:', { conversationId, userId, summaryLength: summary.length, suggestionsCount: suggestions.length });
      
      // Get document_extraction_id if exists
      const { data: extractionData, error: extractionError } = await supabase
        .from('document_extractions')
        .select('id')
        .eq('conversation_id', conversationId)
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (extractionError && extractionError.code !== 'PGRST116') {
        console.warn('Erro ao buscar document_extraction:', extractionError);
      }

      // CORRIGIDO: Usar INSERT ao invés de UPSERT (não há constraint única adequada)
      // Primeiro deletar registros antigos se existirem
      const { error: deleteError } = await supabase
        .from('document_analysis')
        .delete()
        .eq('conversation_id', conversationId)
        .eq('user_id', userId);

      if (deleteError) {
        console.warn('⚠️ Erro ao deletar análise antiga:', deleteError);
      }

      // Inserir nova análise
      const { data: insertData, error: insertError } = await supabase
        .from('document_analysis')
        .insert({
          conversation_id: conversationId,
          document_extraction_id: extractionData?.id || null,
          summary,
          suggestions: JSON.stringify(suggestions),
          document_type: context.documentType,
          key_data: JSON.stringify(context.keyData),
          user_id: userId
        })
        .select();

      if (insertError) {
        console.error('❌ Erro ao salvar document_analysis:', insertError);
        
        // FALLBACK: Salvar em document_extractions como backup
        console.log('🔄 Tentando salvar em document_extractions como fallback...');
        const { error: backupError } = await supabase
          .from('document_extractions')
          .upsert({
            conversation_id: conversationId,
            user_id: userId,
            summary,
            extracted_text: JSON.stringify({ suggestions, context }),
            filename: 'document_analysis_backup'
          }, {
            onConflict: 'conversation_id,user_id'
          });

        if (backupError) {
          console.error('❌ Fallback também falhou:', backupError);
        } else {
          console.log('✅ Backup salvo em document_extractions');
        }
      } else {
        console.log('✅ Document analysis salvo com sucesso:', insertData);
      }
    } catch (error) {
      console.error('❌ Exceção ao salvar análise:', error);
    }
  }

  /**
   * Load document context from database - CORRIGIDO com fallback robusto
   */
  async loadFromDatabase(conversationId: string, userId: string): Promise<DocumentSession | null> {
    try {
      console.log('📂 Carregando análise do banco:', { conversationId, userId });
      
      // PRIMEIRO: Tentar carregar de document_analysis
      const { data: analysisData, error: analysisError } = await supabase
        .from('document_analysis')
        .select('*')
        .eq('conversation_id', conversationId)
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (analysisError && analysisError.code !== 'PGRST116') {
        console.warn('⚠️ Erro ao carregar de document_analysis:', analysisError);
      }

      // Se encontrou dados em document_analysis, usar eles
      if (analysisData && analysisData.summary) {
        console.log('✅ Dados encontrados em document_analysis');
        return this.buildSessionFromAnalysisData(analysisData, conversationId);
      }

      // FALLBACK: Tentar carregar de document_extractions
      console.log('🔄 Tentando fallback em document_extractions...');
      const { data: extractionData, error: extractionError } = await supabase
        .from('document_extractions')
        .select('*')
        .eq('conversation_id', conversationId)
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (extractionError && extractionError.code !== 'PGRST116') {
        console.warn('⚠️ Erro ao carregar de document_extractions:', extractionError);
      }

      if (extractionData && extractionData.summary) {
        console.log('✅ Dados encontrados em document_extractions (fallback)');
        return this.buildSessionFromExtractionData(extractionData, conversationId);
      }

      console.log('📭 Nenhum dado encontrado em ambas as tabelas para:', conversationId);
      return null;

    } catch (error) {
      console.error('❌ Erro ao carregar análise do banco:', error);
      return null;
    }
  }

  /**
   * Constrói sessão a partir dos dados de document_analysis
   */
  private buildSessionFromAnalysisData(data: any, conversationId: string): DocumentSession {
    // Safe JSON parsing with type checking
    let keyData = {
      names: [],
      values: [],
      dates: [],
      processNumbers: [],
      parties: []
    };

    let suggestions: ContextualSuggestion[] = [];

    try {
      if (data.key_data && typeof data.key_data === 'string') {
        keyData = JSON.parse(data.key_data);
      } else if (data.key_data && typeof data.key_data === 'object') {
        keyData = data.key_data as any;
      }
    } catch (e) {
      console.warn('Error parsing key_data:', e);
    }

    try {
      if (data.suggestions && typeof data.suggestions === 'string') {
        suggestions = JSON.parse(data.suggestions) as ContextualSuggestion[];
      } else if (Array.isArray(data.suggestions)) {
        suggestions = data.suggestions as unknown as ContextualSuggestion[];
      }
    } catch (e) {
      console.warn('Error parsing suggestions:', e);
    }

    const session: DocumentSession = {
      conversationId,
      documents: [{
        documentType: data.document_type || 'DOCUMENTO_JURIDICO',
        summary: data.summary,
        keyData
      }],
      lastAnalysis: {
        summary: data.summary,
        suggestions,
        timestamp: new Date(data.created_at)
      }
    };

    // Store in memory cache
    this.sessions.set(conversationId, session);
    
    console.log('📥 Document analysis carregado de document_analysis:', conversationId);
    return session;
  }

  /**
   * Constrói sessão a partir dos dados de document_extractions (fallback)
   */
  private buildSessionFromExtractionData(data: any, conversationId: string): DocumentSession {
    let suggestions: ContextualSuggestion[] = [];
    let keyData = {
      names: [],
      values: [],
      dates: [],
      processNumbers: [],
      parties: []
    };

    // Tentar extrair sugestões do extracted_text se for backup
    try {
      if (data.extracted_text && typeof data.extracted_text === 'string') {
        const parsed = JSON.parse(data.extracted_text);
        if (parsed.suggestions) {
          suggestions = parsed.suggestions;
        }
        if (parsed.context && parsed.context.keyData) {
          keyData = parsed.context.keyData;
        }
      }
    } catch (e) {
      console.warn('Error parsing extracted_text backup:', e);
    }

    const session: DocumentSession = {
      conversationId,
      documents: [{
        documentType: 'DOCUMENTO_JURIDICO',
        summary: data.summary,
        keyData
      }],
      lastAnalysis: {
        summary: data.summary,
        suggestions,
        timestamp: new Date(data.created_at)
      }
    };

    // Store in memory cache
    this.sessions.set(conversationId, session);
    
    console.log('📥 Document analysis carregado de document_extractions (fallback):', conversationId);
    return session;
  }

  /**
   * Get document context for a conversation (try memory first, then database)
   */
  async getDocumentContext(conversationId: string, userId?: string): Promise<DocumentSession | null> {
    // Try memory first
    const memorySession = this.sessions.get(conversationId);
    if (memorySession) {
      return memorySession;
    }

    // Try database if userId provided
    if (userId) {
      return await this.loadFromDatabase(conversationId, userId);
    }

    return null;
  }

  /**
   * Get the latest document summary for a conversation
   */
  async getLatestSummary(conversationId: string, userId?: string): Promise<string | null> {
    const session = await this.getDocumentContext(conversationId, userId);
    return session?.lastAnalysis?.summary || null;
  }

  /**
   * Get contextual suggestions for a conversation
   */
  async getContextualSuggestions(conversationId: string, userId?: string): Promise<ContextualSuggestion[]> {
    const session = await this.getDocumentContext(conversationId, userId);
    return session?.lastAnalysis?.suggestions || [];
  }

  /**
   * Clear context for a conversation
   */
  clearContext(conversationId: string): void {
    this.sessions.delete(conversationId);
    console.log(`🗑️ Document context cleared for conversation ${conversationId}`);
  }

  /**
   * Build context string for AI chat
   */
  async buildContextForChat(conversationId: string, userId?: string): Promise<string> {
    const session = await this.getDocumentContext(conversationId, userId);
    if (!session || !session.lastAnalysis) {
      return '';
    }

    const { summary, timestamp } = session.lastAnalysis;
    const documentsCount = session.documents.length;
    
    return `
CONTEXTO DOS DOCUMENTOS ANALISADOS:
- Documentos na conversa: ${documentsCount}
- Último resumo: ${summary}
- Analisado em: ${timestamp.toLocaleString('pt-BR')}

Use estas informações para dar respostas mais precisas e contextualizadas sobre os documentos enviados.`;
  }
}

// Singleton instance
export const documentContextManager = new DocumentContextManager();
